import React, { useCallback, useEffect, useState } from "react";
import axios from "./axiosConfig";

const ManageLeaguesModal = ({ isOpen, onClose, isFirstSetup }) => {
  // #region State — competition list with membership flags; loading/error for the fetch; per-competition toggling set to prevent double-clicks; emblem load error tracking; join-by-code form state
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [toggling, setToggling] = useState(new Set());
  const [emblemErrors, setEmblemErrors] = useState(new Set());

  const [joinCode, setJoinCode] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [joinMessage, setJoinMessage] = useState("");
  const [joinedPrivateLeague, setJoinedPrivateLeague] = useState(false);
  // #endregion

  // #region Data fetching — fetchCompetitions loads /api/v1/leagues/global-competitions (all supported competitions with isMember flag); called every time the modal opens
  const fetchCompetitions = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const res = await axios.get("/api/v1/leagues/global-competitions");
      setCompetitions(res.data.competitions || []);
    } catch (err) {
      console.error("ManageLeaguesModal fetch error:", err?.response?.status, err?.response?.data || err.message);
      setFetchError("Could not load competitions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCompetitions();
      setJoinCode("");
      setJoinMessage("");
      setJoinedPrivateLeague(false);
    }
  }, [isOpen, fetchCompetitions]);
  // #endregion

  // #region Event handlers — handleToggle posts join or delete leave for a global competition and flips isMember locally; handleJoinByCode joins a private league by code, sets joinedPrivateLeague to satisfy the first-setup gate
  const handleToggle = async (comp) => {
    const id = comp.competitionId;
    if (toggling.has(id)) return;
    setToggling((prev) => new Set([...prev, id]));
    try {
      if (comp.isMember) {
        await axios.delete("/api/v1/leagues/global/leave", {
          data: { competitionId: id },
        });
      } else {
        await axios.post("/api/v1/leagues/global/join", { competitionId: id });
      }
      setCompetitions((prev) =>
        prev.map((c) =>
          c.competitionId === id ? { ...c, isMember: !c.isMember } : c,
        ),
      );
    } catch {
      // silent
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleJoinByCode = async (e) => {
    e.preventDefault();
    const code = joinCode.trim().toUpperCase();
    if (!code) {
      setJoinMessage("Enter a league code.");
      return;
    }
    setIsJoining(true);
    setJoinMessage("");
    try {
      const res = await axios.post(
        `/api/v1/leagues/${encodeURIComponent(code)}/join`,
        { joinCode: code },
      );
      setJoinCode("");
      setJoinMessage(res.data?.message || `Joined league using code ${code}.`);
      setJoinedPrivateLeague(true);
    } catch (err) {
      setJoinMessage(
        err?.response?.data?.message || "Could not join league. Check the code and try again.",
      );
    } finally {
      setIsJoining(false);
    }
  };
  // #endregion

  // #region Render — returns null when closed; shows competition cards with join/leave toggles; join-by-code form for private leagues; footer with first-setup gate warning and Done/Get Started button
  if (!isOpen) return null;

  const hasAnyMember = competitions.some((c) => c.isMember);
  const canClose = !isFirstSetup || hasAnyMember || joinedPrivateLeague;

  return (
    <div className="modal-overlay" onClick={canClose ? onClose : undefined}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Manage Competitions</h2>
        <span >
        <p className="modal-subtitle">
           - Choose which competitions you want to follow.
          </p>
          <p className="modal-subtitle">
            - Joining adds you to
          that competition's Global League and enables matchday reminder and
          results emails. </p>
          <p className="modal-subtitle">
            - Leaving removes you from the leaderboard and stops
          those emails — your predictions and points are always kept and you
          can rejoin at any time.</p>
        </span>
        {loading ? (
          <p className="modal-loading">Loading competitions...</p>
        ) : fetchError ? (
          <p className="modal-warning" style={{ alignSelf: "flex-start" }}>{fetchError}</p>
        ) : (
          <div className="competition-list">
            {competitions.map((comp) => (
              <div
                key={comp.competitionId}
                className={`competition-card${comp.isMember ? " competition-card--joined" : ""}`}
              >
                <div className="competition-emblem-bg">
                  {comp.emblem && !emblemErrors.has(comp.competitionId) ? (
                    <img
                      src={comp.emblem}
                      alt={comp.name}
                      className="competition-emblem"
                      onError={() =>
                        setEmblemErrors((prev) => new Set([...prev, comp.competitionId]))
                      }
                    />
                  ) : (
                    <span className="competition-emblem-code">{comp.code}</span>
                  )}
                </div>
                <span className="competition-name">{comp.name}</span>
                <button
                  className={`competition-toggle${comp.isMember ? " competition-toggle--leave" : " competition-toggle--join"}`}
                  onClick={() => handleToggle(comp)}
                  disabled={toggling.has(comp.competitionId)}
                >
                  {toggling.has(comp.competitionId)
                    ? "..."
                    : comp.isMember
                      ? "Leave"
                      : "Join"}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="modal-join-code-section">
          <p className="modal-subtitle" style={{ marginBottom: "8px" }}>
            Already have a league code? <br />Join a private league directly:
          </p>
          <form className="modal-join-code-form" onSubmit={handleJoinByCode}>
            <input
              className="league-input"
              placeholder="Enter league code"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
              autoComplete="off"
            />
            <button
              type="submit"
              className="submit-btn modal-close-btn"
              disabled={isJoining}
            >
              {isJoining ? "Joining..." : "Join"}
            </button>
          </form>
          {joinMessage && (
            <p className={`helper-text ${joinedPrivateLeague ? "success-text" : "modal-warning"}`}
               style={{ alignSelf: "flex-start", marginTop: "6px" }}>
              {joinMessage}
            </p>
          )}
        </div>

        <div className="modal-footer">
          {isFirstSetup && !hasAnyMember && !joinedPrivateLeague && (
            <p className="modal-warning">
              Join a global competition or enter a league code to continue.
            </p>
          )}
          <button
            className="submit-btn modal-close-btn"
            onClick={onClose}
            disabled={!canClose}
          >
            {isFirstSetup ? "Get Started" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
  // #endregion
};

export default ManageLeaguesModal;
